import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IQuoteStatus, IQuoteableCarriersProduct } from '@boldpenguin/sdk-types';
import { IControlInputEvent, WebComponentInputBase } from 'bqx-application-web-components';
import { Actions } from 'bqx-deal';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, ReplaySubject, Subject, of } from 'rxjs';
import { catchError, filter, finalize, map, pairwise, startWith, switchMap } from 'rxjs/operators';
import { ApplicationQuestionSetCodes, SDKSessionItem } from 'src/app/enums/sdk-application.enum';
import { IApplication, IQuestionSet } from 'src/app/models/application-form/application-form.model';
import { Section } from 'src/app/models/shared/section.model';
import { ApplicationFormAbstractService } from 'src/app/services/application-form/application-form.abstract.service';
import { AbstractDealAnalyticsService } from 'src/app/services/deal-analytics/abstract-deal-analytics.service';
import { InputFocusAbstractService } from 'src/app/services/input-focus/input-focus.abstract.service';
@Component({
    selector: 'appform-sdk-question-set',
    templateUrl: './sdk-question-set.component.html',
    styleUrls: ['./sdk-question-set.component.css'],
    standalone: false
})
export class SdkQuestionSetComponent implements OnInit, OnChanges {
  @Input() activeMenuOrder;
  @Input() activeQuestionSet: IQuestionSet;
  private _bindQuoteUnsub$ = new Subject<any>();
  private _sdkInitData: any;
  private _readOnlyMode$ = new BehaviorSubject<boolean>(false);
  private _appFormState$ = new BehaviorSubject<string>(null);
  private _isQueSetLoading$ = new BehaviorSubject<boolean>(false);
  private _isAppFormLoading$ = new BehaviorSubject<boolean>(false);
  private _isSyncedWithBP$ = new BehaviorSubject<boolean>(false);
  private _areQuotesLoading$ = new BehaviorSubject<boolean>(false);
  private _isBridgingToSDKQSet$ = new Observable<boolean>();
  private requestWindow: Window | null;
  private _activeMenuOrder$ = new ReplaySubject<number>();
  private _activeQuestSetCode$ = new ReplaySubject<string>();
  private _isSaveButtonActive$ = new ReplaySubject<boolean>();
  private _ConsentAcknowledgement$ = new ReplaySubject<boolean>();
  @Input() formData: IApplication;
  public get sdkInitData(): any {
    return this._sdkInitData;
  }

  //This property is mapped to BQX Core as Input property to web components.
  //Keeping this as BehaviorSubject for now, will change this to Observable once changed in BQX Core.
  public get readOnlyMode$(): BehaviorSubject<boolean> {
    return this._readOnlyMode$;
  }

  //This property is mapped to BQX Core as Input property to web components.
  //Keeping this as BehaviorSubject for now, will change this to Observable once changed in BQX Core.
  public get appFormState$(): BehaviorSubject<string> {
    return this._appFormState$;
  }

  public get isBridgingToSDKQSet$(): Observable<boolean> {
    return this._isBridgingToSDKQSet$;
  }

  public get isQueSetLoading$(): Observable<boolean> {
    return this._isQueSetLoading$.asObservable();
  }

  public get isAppFormLoading$(): Observable<boolean> {
    return this._isAppFormLoading$.asObservable();
  }

  public get isSyncedWithBP$(): Observable<boolean> {
    return this._isSyncedWithBP$.asObservable();
  }

  public get areQuotesLoading$(): Observable<boolean> {
    return this._areQuotesLoading$.asObservable();
  }

  public get isEOMLoading$(): Observable<boolean> {
    return this.applicationFormService.isEOMLoading$;
  }
  public get activeMenuOrder$(): Observable<number> {
    return this._activeMenuOrder$.asObservable();
  }
  public get carrierProducts$(): Observable<IQuoteableCarriersProduct[]> {
    return this.applicationFormService.carrierProducts$;
  }
  public get isOwner$(): Observable<boolean> {
    return this.applicationFormService.isOwner$;
  }

  public get isReferred(): boolean {
    return this.applicationFormService.isReferred;
  }

  constructor(
    private applicationFormService: ApplicationFormAbstractService,
    private focus: InputFocusAbstractService,
    private loggerService: LoggerAbstractService,
    private logger: NGXLogger,
    private dealAnalyticsService: AbstractDealAnalyticsService
  ) {
    this.setDealPageEventChanges();
  }

  private setDealPageEventChanges(): void {
    const _valueChanges$ = this._activeQuestSetCode$.pipe(
      startWith(''),
      pairwise(),
      filter(([prev, next]) => prev != next)
    );
    _valueChanges$.subscribe(([prev, next]) => {
      switch (this.activeMenuOrder) {
        case 0: // On Rates page do nothing as this is handled in ngOnChanges
          break;
        case 5: {
          // MQS page
          this.dealAnalyticsService.postDealPageEvent(
            '/application_form',
            this.applicationFormService.activeQuestionSet$.value?.question_set?.title
          );
          break;
        }
        default:
          if (this.applicationFormService.quoteNumber) {
            //  isHandshakeDealCall is used to prevent an extra Deal call.
            //  isHandshakeDealCall is initially false.
            //  If application-form-header.component.ts makes its postDealPageEvent() call, isHandshakeDealCall is set to true.
            //  We immediatly come here and are prevented from making another Deal call.
            //  Instead we reset isHandshakeDealCall to false, allowing future Deal calls to be made.
            if (!this.applicationFormService.isHandshakeDealCall) { 
              this.dealAnalyticsService.postDealPageEvent(null, null);
            } else {
              this.applicationFormService.isHandshakeDealCall = false; 
            }
          }
          break;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.keys(changes).some((k) => k === 'activeQuestionSet')) {
      if (this.formData?.form?.reference_id) {
        this._activeQuestSetCode$.next(this.activeQuestionSet?.question_set?.code);
      }
    }
    if (Object.keys(changes).some((k) => k === 'activeMenuOrder')) {
      if (this.formData?.form?.reference_id) {
        if (changes.activeMenuOrder.currentValue >= 0) {
          this._activeMenuOrder$.next(changes.activeMenuOrder.currentValue);
          if (changes.activeMenuOrder.currentValue === 0)
            this.dealAnalyticsService.postDealPageEvent('/application_form', 'Carrier Results');
        }
      }
    }
  }

  ngOnInit(): void {
    this.initWebComponentData();
    if (!sessionStorage.getItem(SDKSessionItem.UID_TOKEN) && this._appFormState$.getValue() === null)
      this.applicationFormService.loadSdkApplication();
    this._readOnlyMode$ = this.applicationFormService.readOnlyMode$;
    this._appFormState$ = this.applicationFormService.appFormState$;
    this._isQueSetLoading$ = this.applicationFormService.isActiveQuestionSetLoading$;
    this._isAppFormLoading$ = this.applicationFormService.isSDKAppFormLoading$;
    this._isSyncedWithBP$ = this.applicationFormService.isAppSyncedWithBP$;
    this._areQuotesLoading$ = this.applicationFormService.areQuotesLoading$;
    this._isBridgingToSDKQSet$ = this.applicationFormService.isBridgingToSDKQSet$;
    this._activeMenuOrder$.next(this.activeMenuOrder === undefined || 0 ? 0 : this.activeMenuOrder);
    this.applicationFormService.showConsentAcknowledgmentCheckbox$.subscribe((val) => {
      if (val) {
        this._ConsentAcknowledgement$.next(sessionStorage.getItem(this.CONSENT_ACKNOWLEDGEMENT_KEY) === 'true' ? true : false);
        this._isSaveButtonActive$.next(sessionStorage.getItem(this.CONSENT_ACKNOWLEDGEMENT_KEY) === 'true' ? true : false);
      } else {
        this._ConsentAcknowledgement$.next(false);
        this._isSaveButtonActive$.next(true);
      }
    });
  }

  private initWebComponentData() {
    this._sdkInitData = {
      formControlProperties: {
        controls: {
          'bp-save-button': {
            properties: {
              text: 'Next',
              loadingStateText: 'Saving...',
              isActive$: this._isSaveButtonActive$,
              applicationState: this.applicationFormService.appFormState$
            }
          },
          'bp-quote-request': {
            properties: {
              isQuoteRequestAsDialog: true
            }
          },
          'bp-base-input': {
            properties: {
              helpButtonText: '?',
              helpButtonTitle: 'more info',
              helpButtonPopeOverTitle: 'More Info:',
              helpButtonPlacement: 'top-left',
              optionalTag: '(Optional)',
              helpButtonDisabledFocus: true
            }
          },
          'bp-radio-input': {
            properties: {
              groupControls: [
                {
                  ismaster: true,
                  groupQuestionCode: 'progressive_send_to_bold_penguin',
                  groupLabel: {
                    display: true,
                    fieldType: 'label',
                    groupId: 'grp_lbl_consent_acknowledgement',
                    label: `<p> We don’t have a carrier match, but we can transfer you to the Commercial Insurance Center because they work with us to provide access to different companies than we typically do. </p>
                            <p> By continuing, you agree that: </p>
                            <p> You authorize Progressive or any of its business partners to call or text you at the number you provide to respond to your request to purchase goods or services. You also agree that they may use an auto dialing or automated system, artificial and/or prerecorded voice messages, or leave a voicemail. You understand you may incur additional charges from your cell phone provider. You understand your consent is not required for you to purchase any goods or services and that you may revoke your authorization at any time. By providing your consent, you agree that you are a regular user of the telephone number provided and you have the right to consent to be called or texted at this number. </p>
                            <p> You understand the information you provide will be shared with selected insurance companies to obtain a policy, or if unable to find a match, with other trusted providers to help find a commercial insurance solution for your business. Information you provide to these third parties may be shared with Progressive companies for their marketing and other uses. </p>
                            `,
                    helpText: ''
                  },
                  groupControl: {
                    fieldType: 'checkbox',
                    questionId: 'chk_consent_acknowledgement',
                    code: 'Consent_Acknowledgement',
                    label: 'I acknowledge I read the above to the caller.',
                    value$: this._ConsentAcknowledgement$.asObservable(),
                    display: true,
                    errorText: 'Please select a valid answer',
                    groupInputDisplay$: this._ConsentAcknowledgement$.asObservable(),
                    groupInputCustomLabel: 'Would you like to continue?'
                  }
                }
              ]
            }
          }
        }
      }
    };
  }
  readonly CONSENT_ACKNOWLEDGEMENT_KEY = 'isConsentAcknowledged';

  private updateConsentAcknowledgment(event) {
    sessionStorage.setItem(this.CONSENT_ACKNOWLEDGEMENT_KEY, event.event.checked);
    this.onConsentAcknowledgmentValueUpdate(event);
  }

  public onInputValueChange(event: IControlInputEvent) {
    if (event.input.code === 'Consent_Acknowledgement') {
      this._ConsentAcknowledgement$.next(event.event.checked);
      this._isSaveButtonActive$.next(event.event.checked);
      this.updateConsentAcknowledgment(event);
    } else {
      if (this.isAnalyticsChangeAllowed(event.input)) {
        const section: Section = this.dealAnalyticsService.currentSection;
        this.dealAnalyticsService.postDealControlEvent(
          this.getAnalyticsAction(event.input),
          event.input.label,
          section.route,
          section.title,
          section.carrierName,
          this.getAnalyticDataValue(event.input)
        );
      }
    }
  }
  private onConsentAcknowledgmentValueUpdate(event: any) {
    const result: string = event.event?.checked ? 'Check' : 'Un-check';
    this.dealAnalyticsService.postDealControlEvent(Actions.CheckBoxChange, 'Consent_Acknowledgement', null, null, null, result);
  }

  public onSaveButtonClicked(event) {
    this.dealCallOnSaveClick();
    this.applicationFormService.subscribeControlToFocus();
  }

  public onSaveButtonBlurred(event) {
    //TODO: custom events
  }

  public onSaveButtonFocused(event) {
    //TODO: custom events
  }

  public onInputInit(event: IControlInputEvent): void {
    // Added this check to restrict focus emit on read only mode or the Consent_Acknowledgement checkbox initialize.
    if (!this.readOnlyMode$.getValue() && event.input.code !== 'Consent_Acknowledgement') {
      this.focus.emitInputIsSet();
    }
  }

  public onQuoteBindCalled(event): void {
    let bindUrl = '';
    const data = event.data;
    const dealLabel = data.sourceView ? 'Bind Policy - View Details' : 'Bind Policy';
    this.dealAnalyticsService.postDealControlEvent(
      Actions.ButtonClick,
      dealLabel,
      '/application_form',
      'Carrier Results',
      data?.carrierName
    );
    this.applicationFormService
      .bindQuote(data.appFormId, data.quoteId)
      .pipe(
        switchMap((url) => {
          if (url) {
            bindUrl = url;
            this.requestWindow = window.open(url, '_blank');
          }
          return this.applicationFormService.quoteRequests$.getValue();
        }),
        catchError((err: any, caught: Observable<any>) => {
          bindUrl = 'bindQuote() failed';
          this._bindQuoteUnsub$.next(null);
          this._bindQuoteUnsub$.complete();

          this.loggerService.postLogData(LogLevel.Error, 'sdk-question-set:onQuoteBindCalled', err, '');
          return of([]);
        }),
        map((quoteRequest) => {
          if (quoteRequest.selected) {
            this.InvokeCarrierDetailDealEvent(quoteRequest.carrier.name, 'Selected Carrier', null);
          }
        }),
        finalize(() => {
          this.loggerService.postLogData(
            LogLevel.Info,
            'Bind Policy Attempted',
            JSON.stringify({ quoteNumber: this.applicationFormService.quoteNumber, carrier: data?.carrierName, result: bindUrl })
          );
        })
      )
      .subscribe();
  }

  public onExploreMarketsClicked(event): void {
    this.dealAnalyticsService.postDealControlEvent(Actions.ButtonClick, 'Explore Other Markets', '/application_form', 'Carrier Results');
    this.applicationFormService.exploreOtherMarkets();
    sessionStorage.setItem('EOM', 'Y');
  }

  public onQuoteViewDetailsClicked(event): void {
    const data = event.data;
    this.dealAnalyticsService.postDealControlEvent(
      Actions.LinkClick,
      'View Details',
      '/application_form',
      'Carrier Results',
      data?.carrierName
    );
  }

  public onQuoteRequestView(event): void {
    const data = event.data;
    const quoteStatus: IQuoteStatus = data.quoteStatus;
    this.InvokeCarrierDetailDealEvent(data.carrierName, 'Status', data.requestStatus);
    this.InvokeCarrierDetailDealEvent(data.carrierName, 'RateReturned', null, data.quoteInfo?.annual_price);
    if (quoteStatus.recommended) {
      this.InvokeCarrierDetailDealEvent(data.carrierName, 'Reccomended Carrier', null);
    }
    if (quoteStatus.selected) {
      this.InvokeCarrierDetailDealEvent(data.carrierName, 'Selected Carrier', null);
    }
    if (quoteStatus.consumer_selected) {
      this.InvokeCarrierDetailDealEvent(data.carrierName, 'Consumer Selected Carrier', null);
    }
  }

  private InvokeCarrierDetailDealEvent(carrierName: string, label: string, dataValue: string, premiumAmount?: string) {
    this.dealAnalyticsService.postDealControlEvent(
      Actions.Message_Displayed,
      label,
      '/application_form',
      'Carrier Results',
      carrierName,
      dataValue,
      premiumAmount
    );
  }

  private dealCallOnSaveClick() {
    const currentQuestionSet: IQuestionSet = this.applicationFormService.activeQuestionSet$.value;
    if (currentQuestionSet?.question_set.code !== ApplicationQuestionSetCodes.CONSENT) {
      const section: Section = this.dealAnalyticsService.currentSection;
      this.dealAnalyticsService.postDealControlEvent(Actions.ButtonClick, 'Next', section.route, section.title, section.carrierName);
    } else {
      const consentAnswer = currentQuestionSet?.answers[0];
      let selectedAnswer: string = 'Please select a valid answer';
      if (consentAnswer) {
        const selectedChoice = consentAnswer.question.choices?.find((choice) => choice.id === consentAnswer.choice_id);
        if (selectedChoice) {
          selectedAnswer = selectedChoice.value;
        }
      }
      this.dealAnalyticsService.postDealControlEvent(Actions.ButtonClick, 'Next', null, null, null, selectedAnswer);
    }
  }

  private getAnalyticsAction(input: WebComponentInputBase | any): Actions {
    let action: Actions;
    switch (input.fieldType) {
      case 'dropdown':
        action = Actions.DropdownChange;
        break;
      case 'radio':
        action = Actions.RadioButtonChange;
        break;
      case 'checkbox':
      case 'groupcheckbox':
        action = Actions.CheckBoxChange;
        break;
      default:
        action = Actions.Change;
        break;
    }
    return action;
  }

  private isAnalyticsChangeAllowed(input: WebComponentInputBase | any): boolean {
    let allowed = false;
    switch (input.fieldType) {
      case 'radio':
        allowed = true;
        break;
      default:
        break;
    }
    return allowed;
  }

  private getAnalyticDataValue(input): string {
    let dataValue = '';
    switch (input.fieldType) {
      case 'dropdown':
        dataValue = input.choices.find((choice) => choice.id == input.inputControl.value)?.alias;
        break;
      case 'radio':
        dataValue = input.choices.find((choice) => choice.id == input.inputControl.value)?.value;
        break;
      case 'checkbox':
        dataValue = input.inputControl.value;
        break;
      case 'groupcheckbox':
        break;
      default:
        dataValue = input.inputControl.value;
        break;
    }
    return dataValue;
  }
}
